import { Outlet, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { useState, useEffect } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useSidebar } from "@/contexts/SidebarContext";
import { useUpdateNotification } from "@/hooks/useUpdateNotification";
import { Toaster } from "sonner";
import { AiChatWidget } from "@/components/chat/AiChatWidget";
import { AiChatWidgetV2 } from "@/components/chat/AiChatWidgetV2";
import { NotificationPermissionBanner } from "@/components/notifications/NotificationPermissionBanner";
import { FollowedProjectsModal } from "@/components/notifications/FollowedProjectsModal";
import { UpdateFloatingCard } from "@/components/update-notification/UpdateFloatingCard";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { PageLoadingSpinner } from "@/components/ui/LoadingSpinner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { SlidePanel } from "@/components/ui/slide-panel";
import AddUserPage from "@/pages/AddUserPage";

// Robust mobile detection that works even with "Request Desktop Site"
function isMobileDevice(): boolean {
  // Check 1: Touch capability (most reliable)
  const hasTouch = 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 0;

  // Check 2: User agent contains mobile keywords
  const ua = navigator.userAgent.toLowerCase();
  const hasMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(ua);

  // Check 3: High device pixel ratio (typical of mobile)
  const hasHighDPR = window.devicePixelRatio > 1.5;

  // Check 4: Has orientation API (mobile-specific)
  const hasOrientation = 'orientation' in window;

  // Check 5: Screen size is mobile-like
  const hasSmallScreen = window.screen.width <= 768 || window.screen.height <= 768;

  // Return true if at least 2 indicators suggest mobile
  const indicators = [hasTouch, hasMobileUA, hasHighDPR && hasOrientation, hasSmallScreen];
  const mobileCount = indicators.filter(Boolean).length;

  return mobileCount >= 2;
}

export function MainLayout() {
  const { sidebarOpen, sidebarCollapsed, toggleSidebar, toggleSidebarCollapse } = useSidebar();
  const [isAddUserPanelOpen, setIsAddUserPanelOpen] = useState(false);

  // Use robust mobile detection
  const [isMobile, setIsMobile] = useState(isMobileDevice());

  const location = useLocation();

  // Fix mobile viewport height issues with address bar
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      // Debug logging to see what's being detected
      console.log('📱 Mobile Detection Debug:', {
        isMobile,
        detectionSignals: {
          hasTouch: navigator.maxTouchPoints > 0,
          touchPoints: navigator.maxTouchPoints,
          hasMobileUA: /android|mobile|tablet/i.test(navigator.userAgent),
          devicePixelRatio: window.devicePixelRatio,
          hasOrientation: 'orientation' in window,
          screenSize: `${window.screen.width}x${window.screen.height}`
        },
        viewport: {
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          vhValue: `${vh}px`,
          calculatedHeight: vh * 100
        },
        userAgent: navigator.userAgent
      });
    };

    // Set on mount
    setVH();

    // Update on resize and orientation change
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, [isMobile]);

  // Update notification system
  const { showNotification, handleRefresh, handleDismiss } = useUpdateNotification({
    enabled: true, // Enable update checking
  });

  // Get loading states from all contexts
  const {
    user,
    loading: authLoading,
    isImpersonating,
    originalUser,
  } = useAuth();
  const { currentTenant, isLoading: tenantLoading } = useTenant();
  const { loading: permissionLoading, hasPermission } = usePermissions();

  // Check if all contexts are still loading
  const isContextLoading =
    authLoading ||
    tenantLoading ||
    permissionLoading ||
    !user ||
    !currentTenant;

  useEffect(() => {}, [isImpersonating, user, originalUser]);

  // Show loading state while contexts are initializing
  if (isContextLoading) {
    return <PageLoadingSpinner message="Loading your workspace..." />;
  }

  // Ensure all required context data is available
  if (!user || !currentTenant) {
    return <PageLoadingSpinner message="Loading user data..." />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Notification Permission Banner */}
      <div className="relative z-[100]">
        <NotificationPermissionBanner />
      </div>

      {/* Fixed Header */}
      <Header toggleSidebar={toggleSidebar} />

      {/* Impersonation Banner */}
      {isImpersonating && (
        <ImpersonationBanner
          sidebarOpen={sidebarOpen}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}

      {/* Content area with padding for fixed header and banner */}
      <div className={`flex flex-1 ${isImpersonating ? "pt-24" : "pt-14"}`}>
        {/* Mobile overlay when sidebar is open */}
        {sidebarOpen && isMobile && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[100] md:hidden"
            onClick={toggleSidebar}
          />
        )}

        {/* Fixed Sidebar */}
        <div
          className={`transform transition-transform duration-200 ease-in-out fixed top-14 bottom-0 left-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 z-[100]`}
          style={{
            height: isMobile
              ? "calc(var(--vh, 1vh) * 100 - 3.5rem)"
              : "calc(100vh - 3.5rem)"
          }}
        >
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapse}
            onOpenAddUser={() => setIsAddUserPanelOpen(true)}
          />
        </div>

        {/* Main content with margin for sidebar */}
        <main
          className={`pl-3 pr-4 pt-4 w-full transition-all duration-300 ${
            sidebarOpen ? (sidebarCollapsed ? "md:ml-14" : "md:ml-56") : "ml-0"
          }`}
          style={{
            overflowX: "hidden",
            // On mobile, use CSS variable --vh for accurate viewport height
            // On desktop, use standard vh units
            ...(isMobile ? {
              minHeight: isImpersonating ? "calc(var(--vh, 1vh) * 100 - 6rem)" : "calc(var(--vh, 1vh) * 100 - 3.5rem)",
              height: "auto",
              maxHeight: "none"
            } : {
              height: isImpersonating ? "calc(100vh - 6rem)" : "calc(100vh - 3.5rem)",
              maxHeight: isImpersonating ? "calc(100vh - 6rem)" : "calc(100vh - 3.5rem)"
            }),
            overflowY: "auto",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Outlet />
        </main>
      </div>

      {/* Backdrop for mobile - only shown when sidebar is open on mobile */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      <Toaster />
      
      {/* Global Project Modal - listens for openProjectModal events */}
      <FollowedProjectsModal />
      
      {/* Hide AI chat widget on Thryve and Tracking pages */}
      {!location.pathname.includes("/thryve") &&
        !location.pathname.includes("/tracking") && (
          <>
            {/* Show V2 for specific users, V1 for everyone else */}
            {hasPermission('system.override') || ["ben@solerro.com", "rj@solerro.com", "richard@sesolarsolutions.com", "tripper@solerro.com", "kyle@qualitysolarepc.com"].includes(user?.email || "") ? (
              <AiChatWidgetV2 />
            ) : (
              <AiChatWidget />
            )}
          </>
        )}

      {/* Add User Slide Panel */}
      <SlidePanel
        isOpen={isAddUserPanelOpen}
        onClose={() => setIsAddUserPanelOpen(false)}
        title="Add New User"
        width="lg"
        showEdgeCloseButton={true}
        showCloseButton={false}
        topOffset="3.5rem"
      >
        <AddUserPage onClose={() => setIsAddUserPanelOpen(false)} />
      </SlidePanel>

      {/* Update notification - positioned behind sidebar with responsive behavior */}
      {showNotification && (
        <UpdateFloatingCard
          onRefresh={handleRefresh}
          onDismiss={handleDismiss}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}
    </div>
  );
}
